import * as React from 'react';

import ActiveSubscriptions from 'bundles/composable-page-header/components/ActiveSubscriptions';
import CourseraPlusButtonPopoverFooter from 'bundles/composable-page-header/components/coursera-plus-button/CourseraPlusButtonPopoverFooter';
import AccountDropdownGridItemModule from 'bundles/composable-page-header/components/grid-items/account-dropdown/AccountDropdownGridItemModule';
import type {
  DegreeForProgramSwitcher as Degree,
  ThirdPartyOrganizationForProgramSwitcher as ThirdPartyOrganization,
} from 'bundles/composable-page-header/components/program-switcher/types';
import OrganizationList from 'bundles/composable-page-header/components/user-experience-switcher/OrganizationList';
import getDefaultAccountDropdownLinks from 'bundles/composable-page-header/utils/getDefaultAccountDropdownLinks';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  hasEnrolledPrograms: boolean;
  naptimeManagedThirdPartyOrganizations?: Array<{ slug: string; name: string }> | null;
  hasCheatingIncidents: boolean;
  canViewEducatorAdminLink: boolean;
  isCourseraPlusLearner: boolean;
  courseraPlusSubscriptionCycle?: string | null;
  footerCta?: React.ReactNode;
  currentOrganization?: ThirdPartyOrganization | null;
  currentDegree?: Degree | null;
  organizations?: ThirdPartyOrganization[] | null;
  degrees?: Degree[] | null;
};

// Provides default links and options for the account dropdown grid item. This is used in the majority of logged-in headers.
const DefaultAccountDropdownGridItemRenderer = ({
  hasEnrolledPrograms,
  naptimeManagedThirdPartyOrganizations,
  canViewEducatorAdminLink,
  hasCheatingIncidents,
  isCourseraPlusLearner,
  courseraPlusSubscriptionCycle,
  footerCta,
  currentOrganization,
  currentDegree,
  organizations,
  degrees,
}: Props) => {
  const isOrgScopedHome = GrowthDiscoveryEpicClient.preview('orgScopedHome');

  const renderFooterContent = () => {
    if (!!footerCta || (!isCourseraPlusLearner && !hasEnrolledPrograms)) {
      return (
        <React.Fragment>
          {!isCourseraPlusLearner && !hasEnrolledPrograms && (
            <CourseraPlusButtonPopoverFooter deEmphasized={!!footerCta} />
          )}
          {footerCta ?? null}
        </React.Fragment>
      );
    } else {
      return undefined;
    }
  };

  return (
    <AccountDropdownGridItemModule
      dropdownLinks={getDefaultAccountDropdownLinks({
        isDegreeOrEnterpriseLearner: !!hasEnrolledPrograms,
        managedThirdPartyOrganizations: naptimeManagedThirdPartyOrganizations,
        canViewEducatorAdminLink,
        hasCheatingIncidents,
      })}
      footer={renderFooterContent()}
      header={
        !!courseraPlusSubscriptionCycle && (
          <ActiveSubscriptions
            title={_t('Your current subscription')}
            subtitle={
              courseraPlusSubscriptionCycle === 'MONTHLY' ? _t('Coursera Plus Monthly') : _t('Coursera Plus Annual')
            }
          />
        )
      }
      experienceSwitcher={
        isOrgScopedHome &&
        hasEnrolledPrograms && (
          <OrganizationList
            currentOrganization={currentOrganization}
            currentDegree={currentDegree}
            organizations={organizations}
            degrees={degrees}
          />
        )
      }
      isCourseraPlusLearner={isCourseraPlusLearner}
      // This hides the border separating links to not imply the C+ link is related
      // to the other CTA in footer when present
      hideFooterBorder={!!footerCta}
    />
  );
};

export default DefaultAccountDropdownGridItemRenderer;
